<script>
import SwitchComponent from '/dashboard/components-next/switch/Switch.vue';
import { formatDate } from 'dashboard/helper/dateHelper.js';
import SuspendMenu from '../../../../components/SuspendMenu.vue';
export default {
  name: 'Card',
  components: {
    SwitchComponent,
    SuspendMenu,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
    postType: {
      type: String,
      default: '',
    },
  },
  emits: ['toggleActive', 'testMode', 'deletedPost'],
  data() {
    return {
      showReadMore: false,
      isSwitchOn: false,
      isCheckingPost: false,
    };
  },
  computed: {
    isVideo() {
      return this.post.image_path && this.post.image_path.endsWith('.mp4');
    },
  },
  methods: {
    formatDate(date) {
      return formatDate(date);
    },
    async handleClick() {
      if (this.post.comment_count === 0) {
        this.isCheckingPost = true;
        try {
          await this.$store.dispatch('comments/getPostInfo', {
            pageId: this.post.channel_facebook_page_id,
            postId: this.post.id,
          });
          this.navigateToPost();
        } catch (e) {
          if (e.deletedPost) {
            this.$emit('deletedPost', e.deletedPost);
            return;
          }
          throw e;
        } finally {
          this.isCheckingPost = false;
        }
        return;
      }
      this.navigateToPost();
    },
    navigateToPost() {
      const routeName = this.postType
        ? `${this.postType}_posts_comments`
        : 'posts_comments';

      const returnUrl = this.$router.resolve({
        name: this.$route.name,
        params: this.$route.params,
        query: this.$route.query,
      }).href;

      this.$router.push({
        name: routeName,
        params: {
          page_id: this.post.channel_facebook_page_id,
          post_id: this.post.id,
        },
        query: {
          returnTo: returnUrl,
        },
      });
    },
    handleReadMore() {
      this.showReadMore = !this.showReadMore;
    },
    toggleActive() {
      this.$emit('toggleActive', this.post.id);
    },
    handleTestMode() {
      this.$emit('testMode', {
        showAITestChat: true,
        selectedPost: this.post,
      });
    },
    togglePlay(event) {
      const video = this.$refs.videoElement;
      if (!video) return;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
      event.stopPropagation();
    },
  },
};
</script>

<template>
  <article
    class="group flex flex-col h-full bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-300 ease-out overflow-visible"
  >
    <div
      class="flex justify-between items-center gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 dark:border-slate-700/50"
    >
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <div
          class="flex-shrink-0 w-2 h-2 rounded-full transition-colors duration-200"
          :class="
            post.active
              ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]'
              : 'bg-slate-300 dark:bg-slate-600'
          "
        />
        <h5
          class="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate"
        >
          {{ $t('COMMENTS.CARD.ALLOW_REPLIES') }}
        </h5>
      </div>

      <div class="flex items-center gap-2 flex-shrink-0">
        <SwitchComponent
          v-model="isSwitchOn"
          :model-value="!!post.active"
          @click="toggleActive"
        />
        <SuspendMenu>
          <ul class="list-none m-0 p-0">
            <li
              class="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-all duration-150 first:rounded-t-xl last:rounded-b-xl"
              @click="handleTestMode"
            >
              <span>{{ $t('COMMENTS.CARD.TEST_MODE') }}</span>
            </li>
          </ul>
        </SuspendMenu>
      </div>
    </div>

    <div
      class="relative bg-slate-50 dark:bg-slate-900/50 overflow-hidden"
      :class="isCheckingPost ? 'cursor-wait' : 'cursor-pointer'"
      @click="handleClick"
    >
      <template v-if="isVideo">
        <div class="relative aspect-square w-full">
          <video
            ref="videoElement"
            class="w-full h-full object-cover"
            :src="post.image_path"
            preload="metadata"
            controls
            @click.stop="togglePlay"
          />
        </div>
      </template>

      <template v-else>
        <div class="relative aspect-square w-full overflow-hidden">
          <img
            class="w-full h-full object-cover transition-transform duration-500 ease-out"
            :class="isCheckingPost ? '' : 'group-hover:scale-105'"
            :src="post.image_path || '/assets/images/placeholder.svg'"
            alt="Imagem da publicação"
          />
        </div>
      </template>
    </div>

    <div class="flex flex-col flex-1 px-4 sm:px-5 py-4 gap-3">
      <p class="text-xs text-slate-500 dark:text-slate-400">
        {{ 'Publicado em ' + formatDate(post.created_at) }}
      </p>

      <div class="flex-1 min-h-[60px]">
        <div v-if="post.message" class="space-y-2">
          <p
            class="text-sm text-slate-700 dark:text-slate-300 leading-relaxed"
            :class="showReadMore ? '' : 'line-clamp-3'"
          >
            {{ post.message }}
          </p>
          <button
            v-if="post.message.length > 120"
            class="text-xs font-medium text-woot-600 hover:text-woot-700 dark:text-woot-400 dark:hover:text-woot-300 hover:underline transition-colors duration-150"
            @click.stop="handleReadMore"
          >
            {{
              showReadMore
                ? $t('COMMENTS.CARD.SHOW_LESS')
                : $t('COMMENTS.CARD.READ_MORE')
            }}
          </button>
        </div>
        <p v-else class="text-sm text-slate-400 dark:text-slate-500 italic">
          {{ $t('POSTS.NO_DESCRIPTION') }}
        </p>
      </div>

      <button
        class="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200 transform"
        :class="
          isCheckingPost
            ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 cursor-wait'
            : post.comment_count > 0
              ? 'bg-gradient-to-r from-woot-500 to-woot-600 hover:from-woot-600 hover:to-woot-700 text-white shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
              : 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-600 cursor-pointer'
        "
        :disabled="isCheckingPost"
        @click="handleClick"
      >
        <span v-if="isCheckingPost">{{
          $t('COMMENTS.POST_DETAILS.LOADING')
        }}</span>
        <span v-else>{{ post.comment_count + ' comentário(s)' }}</span>
      </button>
    </div>
  </article>
</template>
